// studioCrumbs — the Editor's title is its breadcrumb (pen E; PS-46):
// Projects / {project} › Module 1 › 3 Quiz – what a plant needs. Short of
// room, the middle steps give way first: each shortens to its name before
// " – " (or its first letters), then the outermost fold into one "…". The
// first and the last step keep their names (the last truncates in the view
// if it must).
// The full name is always on hover. Pure; the room is counted in
// characters, a stand-in for width that needs no measuring.

export { fitCrumbs };

type CrumbStep = {
    // The level it goes back to (null: the folded middle, not a step).
    readonly id: string | null;
    readonly title: string;
    // What it shows.
    readonly label: string;
};

// The longest a shortened step gets before it ends in "…".
const SHORT = 14;

const shorten = (title: string): string => {
    const head = title.split(/\s[–—-]\s|:\s/)[0]?.trim() ?? title;
    return head.length <= SHORT
        ? head
        : `${head.slice(0, SHORT - 1).trimEnd()}…`;
};

const lengthOf = (steps: readonly CrumbStep[]): number =>
    steps.reduce((n, s) => n + s.label.length + 3, 0);

// `steps`: from the outermost level down (the project first, the level
// shown last); `room`: how many characters fit.
const fitCrumbs = (
    steps: readonly { readonly id: string; readonly title: string }[],
    room: number,
): readonly CrumbStep[] => {
    const full = steps.map((s) => ({ ...s, label: s.title }));
    if (full.length <= 2 || lengthOf(full) <= room) return full;
    const first = full[0];
    const last = full[full.length - 1];
    if (first === undefined || last === undefined) return full;
    const middle = full.slice(1, -1);
    // One middle step shortened at a time, the outermost first.
    const shortened = middle.reduce<readonly CrumbStep[]>(
        (done, step, i) =>
            lengthOf([first, ...done, last]) <= room
                ? done
                : done.map((s, j) =>
                      j === i ? { ...s, label: shorten(step.title) } : s,
                  ),
        middle,
    );
    // Then the outermost middle steps fold into one "…", as few as fit.
    const foldedOf = (n: number): readonly CrumbStep[] =>
        n === 0
            ? shortened
            : [
                  {
                      id: null,
                      title: middle
                          .slice(0, n)
                          .map((s) => s.title)
                          .join(" › "),
                      label: "…",
                  },
                  ...shortened.slice(n),
              ];
    const fits = middle
        .map((_, i) => foldedOf(i))
        .find((m) => lengthOf([first, ...m, last]) <= room);
    return [first, ...(fits ?? foldedOf(middle.length)), last];
};
